import { Check, Keyboard, QrCode, Copy, CheckCheck } from 'lucide-react';
import { useState } from 'react';

const PairingInstructions = () => {
    const isFromAddScreen = new URLSearchParams(window.location.search).get('source') === 'add_screen';
    const [isCodeCopied, setIsCodeCopied] = useState(false);
    return (
        <div className="flex flex-col gap-5 text-gray-400 justify-center items-center opacity-75">
            <div className={`flex flex-col gap-3.5 transition-all transform p-6 rounded-xl backdrop-blur-sm`}>
                {/* Step 1 */}
                {isFromAddScreen ? (
                    <div className="flex items-center gap-3.5 group">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors group-hover:bg-zinc-700/50">
                            <QrCode className="w-4.5 h-4.5 text-yellow-300/90 transition-opacity group-hover:opacity-100" />
                        </div>
                        <div className="flex items-center justify-between w-full">
                            <div>
                                <p className="font-medium text-gray-300 transition-colors group-hover:text-gray-200 flex items-center gap-2">
                                    Look at the Code Above
                                    <span className="flex items-center">
                                        <CopyButton onCopy={() => setIsCodeCopied(true)} />
                                    </span>
                                </p>
                                <p className="text-sm text-gray-500">Remember this pairing code - you'll need it in a moment</p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-3.5 group">
                        <div className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors group-hover:bg-zinc-700/50">
                            <QrCode className="w-4.5 h-4.5 text-yellow-300/90 transition-opacity group-hover:opacity-100" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-300 transition-colors group-hover:text-gray-200">Visit <a href="https://app.signagestudio.io" target="_blank" rel="noopener noreferrer" className="text-yellow-300/90 hover:text-yellow-300 transition-colors duration-200">app.signagestudio.io</a></p>
                            <p className="text-sm text-gray-500">Open the web portal in your browser</p>
                        </div>
                    </div>
                )}

                {/* Step 2 */}
                <div className="flex items-center gap-3.5 group">
                    <div className="flex-shrink-0 w-9 h-9 bg-zinc-800/40 rounded-full flex items-center justify-center transition-colors group-hover:bg-zinc-700/50">
                        <Keyboard className="w-4.5 h-4.5 text-yellow-300/90 transition-opacity group-hover:opacity-100" />
                    </div>
                    <div>
                        <p className="font-medium text-gray-300 transition-colors group-hover:text-gray-200">{isFromAddScreen ? 'Go Back to Previous Tab' : 'Enter the Pairing Code'}</p>
                        <p className="text-sm text-gray-500">{isFromAddScreen ? `Switch to the SignageStudio tab and ${isCodeCopied ? "paste" : "enter"} the code` : 'Type the code shown above to connect'}</p>
                    </div>
                </div>

                {/* Step 3 */}
                <div className="flex items-center gap-3.5 group">
                    <div className="flex-shrink-0 w-9 h-9 bg-zinc-800/40 rounded-full flex items-center justify-center transition-colors group-hover:bg-zinc-700/50">
                        <Check className="w-4.5 h-4.5 text-yellow-300/90 transition-opacity group-hover:opacity-100" />
                    </div>
                    <div>
                        <p className="font-medium text-gray-300 transition-colors group-hover:text-gray-200">Start Managing</p>
                        <p className="text-sm text-gray-500">Your screen is ready to display content</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const CopyButton = ({ onCopy }: { onCopy: () => void }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            const code = document.querySelector('[aria-hidden="true"] .font-ibmPlex')?.textContent?.replace(/\s/g, '');
            if (code) {
                await navigator.clipboard.writeText(code);
                setCopied(true);
                onCopy();
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <button
            onClick={handleCopy}
            className={`flex gap-1 items-center justify-center p-2 rounded-md transition-all duration-200 ${copied ? 'bg-green-500/10 text-green-500' : 'hover:bg-zinc-700/50 text-yellow-300/90 hover:text-yellow-300'}`}
            title={copied ? 'Copied!' : 'Copy code'}
        >
            {copied ? <CheckCheck className="w-4.5 h-4.5" /> : <Copy className="w-4.5 h-4.5" />}
            <span className='text-sm text-gray-500'>
                {copied ? 'copied!' : 'click to copy code'}
            </span>
        </button>
    );
};

export default PairingInstructions;